/**
 * Error codes — plan §2.4. Every API error is `{ code, message, field?, details? }`
 * and every code lives here so client and server cannot drift.
 */
export const ERROR_CODES = [
    'unauthenticated',
    'session_expired',
    'step_up_required',
    'forbidden',
    'not_found',
    'validation_failed',
    'version_conflict',
    /*
     * Something is already spoken for — a piece of equipment booked on another
     * visit in the same hours, and anything else an exclusion constraint
     * refuses. Distinct from `version_conflict`, which is somebody editing the
     * same row: here nothing was edited and nothing will be until a person
     * chooses differently.
     */
    'resource_conflict',
    /*
     * A bulk selection no longer describes what it described a moment ago.
     *
     * Its own code rather than `resource_conflict`, because the client's answer
     * is specific and automatic: re-open the confirm with the new number
     * (`details.now`) rather than tell somebody something went wrong. Between
     * reading "126" and pressing Deactivate, a 127th match can appear, and
     * acting on it silently is the one failure "select all matching" must not
     * have (list engine v3 §3.5).
     */
    'selection_changed',
    /*
     * The quote moved while the customer was reading it.
     *
     * Its sibling above is about a *list* changing under a bulk action; this is
     * about an **offer** changing under a signature. `proposalRev` is a hash of
     * everything the page drew, so a price edited, a visit removed or a word
     * changed between the page loading and Accept being pressed answers 409 and
     * writes nothing — because what would otherwise be signed is a document
     * nobody read (docs/proposal.md §4.5, §7.5).
     */
    'proposal_changed',
    /*
     * More rows than one action may reach at once. Not a technical limit — the
     * UPDATE would be fine — but the point past which somebody has almost
     * certainly not meant what they pressed, and the honest answer is to make
     * them narrow the filter.
     */
    'too_many',
    'idempotency_mismatch',
    'idempotency_in_progress',
    'rate_limited',
    'invalid_transition',
    'tenant_context_missing',
    'scope_denied',
    'mfa_required',
    'mfa_invalid',
    'credentials_invalid',
    'token_invalid',
    'token_expired',
    'pin_locked',
    'device_not_registered',
    /*
     * An outside service refused, or is not configured to be asked. Distinct
     * from `internal_error` because it is not our bug and the fix is not ours
     * either: somebody sets a key, or the provider comes back.
     */
    'integration_failed',
    /*
     * A visit would follow itself, directly or through another
     * (docs/visits-billing-and-proposal.md §4.3). The builder's Which-visit
     * list never offers one, so this is the server refusing what no screen
     * should be able to ask for.
     */
    'visit_cycle',
    /*
     * The company has switched this kind of visit off (§4.5.1). A visit that
     * already has it keeps it; only setting it anew is refused.
     */
    'visit_kind_disabled',
    /*
     * A billing plan that would invoice more than the work it holds
     * (docs/visits-billing-and-proposal.md §7.3): a flat deposit above the plan
     * total, or flat stages that add to more than it. Refused rather than
     * clamped, because a clamp is the server quietly deciding a number the
     * quoter typed.
     */
    'deposit_exceeds_total',
    'stages_exceed_total',
    /*
     * An approval whose visits could not all be made (`docs/visit-page.md`
     * §26.3, Board 17 G): the whole approval rolled back, nothing changed, and
     * `details.supportCode` names the `approval_failures` row that says why.
     */
    'approval_visits_failed',
    /*
     * The Visit page (`docs/visit-page.md` §2.6, §9.4 — `VISIT_PAGE_ERRORS`):
     * a write to a closed visit other than an adjustment, a note, photo sharing
     * or Files; a swap on a line a placed PO, a delivery, the truck or the day
     * has already taken.
     */
    'visit_closed',
    'swap_blocked_po_placed',
    /*
     * Job stages (`docs/job-stages.md` §2, §9.4, §5 — the words are §14's):
     * a status that only follows the job's quotes and visits, picked by hand;
     * a second approved quote on a job; approving a version a newer one
     * superseded; a quote sent, marked or approved on a job that is not open
     * for one.
     */
    'use_the_action',
    'agreement_exists',
    'quote_replaced',
    'job_not_open',
    'internal_error',
];
/**
 * The words of job-stages.md §14's four refusals, once, for the server that
 * says them and any page that has to recognise one.
 */
export const JOB_STAGES_ERROR_TEXT = {
    use_the_action: 'That status follows what happens to the job’s quotes and visits.',
    agreement_exists: 'This job already has an approved quote. Start a change order to change it.',
    quote_replaced: 'A newer version of this quote was sent. Open it to approve.',
    job_not_open: 'This job isn’t open for quotes — reopen the request first.',
};
const DEFAULT_STATUS = {
    unauthenticated: 401,
    session_expired: 401,
    step_up_required: 401,
    forbidden: 403,
    not_found: 404,
    validation_failed: 400,
    version_conflict: 409,
    resource_conflict: 409,
    selection_changed: 409,
    proposal_changed: 409,
    too_many: 422,
    idempotency_mismatch: 422,
    idempotency_in_progress: 409,
    rate_limited: 429,
    invalid_transition: 422,
    tenant_context_missing: 500,
    scope_denied: 403,
    mfa_required: 401,
    mfa_invalid: 401,
    credentials_invalid: 401,
    token_invalid: 400,
    token_expired: 400,
    pin_locked: 423,
    device_not_registered: 403,
    // 502: the request was fine and we are fine; something we depend on is not.
    integration_failed: 502,
    visit_cycle: 422,
    visit_kind_disabled: 422,
    deposit_exceeds_total: 422,
    stages_exceed_total: 422,
    approval_visits_failed: 409,
    visit_closed: 409,
    swap_blocked_po_placed: 409,
    use_the_action: 409,
    agreement_exists: 409,
    quote_replaced: 409,
    job_not_open: 409,
    internal_error: 500,
};
export class ApiError extends Error {
    code;
    status;
    field;
    details;
    constructor(code, message, options = {}) {
        super(message);
        this.name = 'ApiError';
        this.code = code;
        this.status = options.status ?? DEFAULT_STATUS[code];
        this.field = options.field;
        this.details = options.details;
    }
    toBody() {
        const body = { code: this.code, message: this.message };
        if (this.field)
            body.field = this.field;
        if (this.details)
            body.details = this.details;
        return body;
    }
}
export const statusForCode = (code) => DEFAULT_STATUS[code];
//# sourceMappingURL=errors.js.map